"use client";

import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import { FlexColumn, FlexColumnAlignJustifyCenter, FlexRowAlignCenter } from "@/components/Flex";
import { AuthEvent } from "@/features/Auth/AuthPopup/utils/constants";
import { BulkPlans } from "@/features/BulkPricingPage/components/BulkPricingComponents";
import { useCheckAuth } from "@/hooks/useCheckAuth";
import { borderRadius } from "@/utils/borderRadius";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import noop from "@knowt/syncing/utils/noop";
import { numberWithCommas } from "@knowt/syncing/utils/stringUtils";
import CheckIcon from "@mui/icons-material/Check";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { SxProps } from "@mui/material/styles";
import Image from "next/image";
import usePricingCardsUtils from "../../hooks/usePricingCardsUtils";
import { PricingCardProps } from "../../utils/constants";
import Mixpanel from "@/utils/analytics/Mixpanel";

export const PromoBadge = ({ text, backgroundColor }: { text: React.ReactNode; backgroundColor: string }) => {
    return (
        <FlexColumnAlignJustifyCenter style={{ backgroundColor, padding: "0.5rem 2rem" }}>
            <span className="bold" style={{ fontSize: "1.5rem", whiteSpace: "nowrap" }}>
                {text}
            </span>
        </FlexColumnAlignJustifyCenter>
    );
};

export default function PricingCard({
    type,
    interval,
    price,
    pricePerMonth,
    priceId,
    isCurrentPlan,
    features = [],
    description,
    buttonText,
    onClick = noop,
    icon,
    background,
    buttonBackground,
    renderTopBadge,
    oldPrice,
    sx,
    btnSx,
    intervalText: intervalText_,
    isSimpleClick,
    isPicker,
    needContactUs,
    nbrLicenses,
    isForBulkPricing,
    specialBenefit,
    upTo,
    perfectFor,
    minimumPurchase,
    isCustom,
    benefitCheckStyle,
}: Omit<PricingCardProps, "type"> & {
    type: string;
    sx?: SxProps;
    btnSx?: SxProps;
    intervalText?: string;
    isSimpleClick?: boolean;
    isForBulkPricing?: boolean;
    benefitCheckStyle?: React.CSSProperties;
}) {
    const { checkAuth } = useCheckAuth();
    const { isSubscriptionCancelling, isFreeTrialAllowed } = useCurrentUser();

    const isDarkBackground = background === themeColors.neutralBlack;

    const { handleSubscribeEvent, isLoading, reactivateSubscription } = usePricingCardsUtils({
        priceId,
        onClick,
        isCurrentPlan,
    });

    const intervalPrice = needContactUs ? "Contact Us" : !isPicker && interval === "Year" ? pricePerMonth : price;

    const intervalText =
        intervalText_ ||
        (isPicker
            ? "per teacher annually"
            : interval === "Year"
              ? `per month, billed ${price} ${isFreeTrialAllowed && !isPicker ? "after trial end" : "annually"}`
              : "billed monthly");

    const discount =
        intervalPrice && oldPrice
            ? 1 - Number(intervalPrice.slice(1)) / Number((oldPrice || intervalPrice).slice(1))
            : 0;

    const renderCardHeader = () => {
        return isForBulkPricing ? (
            <FlexColumn style={{ gap: "1.5rem", color: themeColors.neutralBlack }}>
                <span className="secondaryTextBold1" style={{ color: themeColors.primary }}>
                    Up to {upTo} Users
                </span>
                <FlexRowAlignCenter style={{ gap: "1rem", fontSize: "2.6rem" }}>
                    <span style={{ fontWeight: "700", fontSize: "3rem" }}>{isCustom ? "Custom" : intervalPrice}</span>
                    {!needContactUs && !isCustom && (
                        <span className={"secondaryText1"} style={{ marginTop: spacing.XS }}>
                            {intervalText}
                        </span>
                    )}
                </FlexRowAlignCenter>
                <span className={"secondaryText2"}>{perfectFor}</span>
            </FlexColumn>
        ) : (
            <>
                <FlexRowAlignCenter
                    style={{ gap: "1rem", fontSize: "2.6rem", justifyContent: "space-between", fontWeight: "bold" }}>
                    <Image src={icon} width="42" height="42" alt={"plan type icon"} />
                    {(nbrLicenses || !!discount) && (
                        <FlexRowAlignCenter
                            style={{
                                backgroundColor: themeColors.primaryLight,
                                padding: `${spacing.XS} ${spacing.SM}`,
                                borderRadius: borderRadius.shortInput,
                            }}>
                            <span className="secondaryTextBold1" style={{ color: themeColors.pureBlack }}>
                                {nbrLicenses
                                    ? `${numberWithCommas(nbrLicenses.toString())} plan${nbrLicenses === 1 ? "" : "s"}`
                                    : `Saving ${Math.round(discount * 100)}%`}
                            </span>
                        </FlexRowAlignCenter>
                    )}
                </FlexRowAlignCenter>

                <h2 style={{ fontSize: "2.5rem" }}>{type}</h2>

                <FlexColumn className="bold" as={"p"}>
                    <FlexRowAlignCenter
                        style={{ gap: "1.2rem", marginBottom: needContactUs ? "2.2rem" : "unset" }}
                        as="span">
                        <span style={{ fontWeight: "600", fontSize: "3.5rem" }}>{intervalPrice}</span>
                        {oldPrice && intervalPrice !== oldPrice && (
                            <s style={{ fontWeight: "600", fontSize: "2.5rem", color: themeColors.neutral2 }}>
                                {oldPrice}
                            </s>
                        )}
                    </FlexRowAlignCenter>
                    {!needContactUs && (
                        <span style={{ marginTop: "1rem", fontWeight: "400", fontSize: "1.2rem" }}>{intervalText}</span>
                    )}
                </FlexColumn>
            </>
        );
    };

    const renderCardMiddle = () => {
        return !isForBulkPricing && buttonText ? (
            <CircularRectTextButton
                disabled={isCurrentPlan && !isPicker}
                onClick={
                    isSimpleClick
                        ? onClick
                        : isSubscriptionCancelling
                          ? reactivateSubscription
                          : () =>
                                checkAuth({
                                    event: AuthEvent.CHOOSE_PLAN,
                                    onAuthSuccess: () => handleSubscribeEvent(),
                                })
                }
                sx={{
                    width: "20rem",
                    height: "4.2rem",
                    borderRadius: "5rem",
                    margin: `0 auto ${spacing.XS_2}`,
                    color:
                        buttonBackground === themeColors.neutralBlack
                            ? themeColors.neutralWhite
                            : buttonBackground === themeColors.primary
                              ? themeColors.primaryDark
                              : themeColors.neutralBlack,
                    backgroundColor: buttonBackground ?? "transparent",
                    border: !buttonBackground ? `1px solid ${themeColors.neutralBlack}` : "none",
                    ...(isCurrentPlan &&
                        !isPicker && {
                            backgroundColor: themeColors.neutral1,
                            color: themeColors.neutralBlack,
                        }),
                    ":hover":
                        background === themeColors.neutralBlack
                            ? {
                                  backgroundColor: themeColors.neutralWhite,
                                  color: themeColors.neutralBlack,
                              }
                            : undefined,
                    ...btnSx,
                }}>
                {isLoading ? <CircularProgress size="2.5rem" sx={{ color: themeColors.pureWhite }} /> : buttonText}
            </CircularRectTextButton>
        ) : null;
    };

    const renderSpecialBenefit = () => {
        return (
            <FlexRowAlignCenter
                className="secondaryTextBold1"
                style={{
                    gap: spacing.XS,
                    padding: spacing.XS,
                    borderRadius: "0.7rem",
                    ...specialBenefit?.style,
                }}>
                {specialBenefit?.icon}
                <span style={{ fontSize: "1.2rem" }}>{specialBenefit?.benefit}</span>
            </FlexRowAlignCenter>
        );
    };

    return (
        <FlexColumn
            as={Box}
            component="section"
            sx={{
                color: needContactUs
                    ? themeColors.pureBlack
                    : isDarkBackground
                      ? themeColors.neutralWhite
                      : themeColors.neutralBlack,
                "&:hover": { transform: "translateY(-2px) scale(1.005) translateZ(0)" },
                transition: "transform 0.2s ease-out, box-shadow 0.2s ease-out",
                backgroundColor: background ?? themeColors.neutralWhite,
                padding: "4rem 3rem 5rem 3rem",
                borderRadius: "2rem",
                height: "70rem",
                width: "35rem",
                gap: "2rem",
                position: "relative",
                ...sx,
            }}>
            {renderTopBadge && (
                <div style={{ position: "absolute", top: "-1.5rem", right: "50%", transform: "translateX(50%)" }}>
                    {renderTopBadge}
                </div>
            )}

            {renderCardHeader()}

            {renderCardMiddle()}

            {!BulkPlans && <p style={{ fontSize: "1.5rem", fontWeight: "600" }}>{description}</p>}
            <ul className="strippedList">
                {isForBulkPricing && specialBenefit && (
                    <li key={`special-feature-${type}`} style={{ marginBottom: "2rem" }}>
                        {renderSpecialBenefit()}
                    </li>
                )}
                {features.map((feature, index) => {
                    return (
                        <li key={index} style={{ marginBottom: "2rem" }}>
                            <FlexRowAlignCenter style={{ gap: "1rem" }}>
                                <FlexColumnAlignJustifyCenter
                                    style={{
                                        borderRadius: "999px",
                                        height: "2.3rem",
                                        minWidth: "2.3rem",
                                        maxWidth: "2.3rem",
                                        backgroundColor: isDarkBackground ? themeColors.pureWhite : "#000",
                                        color: isDarkBackground ? "#000" : themeColors.pureWhite,
                                        ...benefitCheckStyle,
                                    }}>
                                    <CheckIcon
                                        sx={{
                                            fontSize: "1.7rem",
                                        }}
                                    />
                                </FlexColumnAlignJustifyCenter>
                                <p style={{ fontSize: "1.5rem" }}>{feature}</p>
                            </FlexRowAlignCenter>
                        </li>
                    );
                })}
            </ul>

            {isForBulkPricing && (
                <FlexColumnAlignJustifyCenter
                    style={{
                        textAlign: "center",
                        flex: 1,
                        justifyContent: "flex-end",
                        gap: spacing.XS_2,
                        paddingBottom: needContactUs ? spacing.SM : undefined,
                    }}>
                    <CircularRectTextButton
                        disabled={isCurrentPlan && !isPicker}
                        onClick={() => {
                            Mixpanel.track("Contact Sales - Clicked");

                            if (isSimpleClick) {
                                onClick();
                            } else if (isSubscriptionCancelling) {
                                reactivateSubscription();
                            } else {
                                checkAuth({
                                    event: AuthEvent.CHOOSE_PLAN,
                                    onAuthSuccess: () => handleSubscribeEvent(),
                                });
                            }
                        }}
                        sx={{
                            width: "20rem",
                            height: "4.2rem",
                            borderRadius: "1rem",

                            color: isForBulkPricing
                                ? themeColors.neutralWhite
                                : buttonBackground === themeColors.neutralBlack
                                  ? themeColors.neutralWhite
                                  : buttonBackground === themeColors.primary
                                    ? themeColors.primaryDark
                                    : themeColors.neutralBlack,
                            backgroundColor: isForBulkPricing
                                ? themeColors.neutralBlack
                                : (buttonBackground ?? "transparent"),
                            border: !buttonBackground ? `1px solid ${themeColors.neutralBlack}` : "none",
                            ...(isCurrentPlan &&
                                !isPicker && {
                                    backgroundColor: themeColors.neutral1,
                                    color: themeColors.neutralBlack,
                                }),
                            ":hover":
                                background === themeColors.neutralBlack
                                    ? {
                                          backgroundColor: themeColors.neutralWhite,
                                          color: themeColors.neutralBlack,
                                      }
                                    : undefined,
                            ...btnSx,
                        }}>
                        {isLoading ? (
                            <CircularProgress size="2.5rem" sx={{ color: themeColors.pureWhite }} />
                        ) : (
                            "Contact Sales"
                        )}
                    </CircularRectTextButton>
                    <span className="secondaryText2">
                        {minimumPurchase ? `Minimum purchase of $${minimumPurchase}` : "No minimum purchase"}
                    </span>
                </FlexColumnAlignJustifyCenter>
            )}
        </FlexColumn>
    );
}
